<template>
  <Link :to="propertyLink" :decoration="false">
    <Card variant="secondary" size="medium" class="property-card">
      <View gap="md" direction="column">
        <View gap="md">
          <PropertyPhotoPreview :image-url="property.coverImage" size="md" />

          <View direction="column" gap="xs" justify="space-between">
            <View direction="column" gap="xs">
              <Text variant="h5">{{ title ?? property.displayName }}</Text>
              <Text variant="body-medium">{{ additionalInfo }}</Text>
            </View>
            <Text v-if="property.leadsCount" variant="body-medium" color="secondary">
              {{ property.leadsCount }} {{ pluralize('lead', property.leadsCount) }}
            </Text>
          </View>
        </View>

        <!-- Only once the company has portfolios or more than one seat: a lone admin already knows who runs what -->
        <View v-if="showPortfolio || showManager" gap="sm" align="center" wrap="wrap" class="team">
          <PortfolioChip
            v-if="showPortfolio && property.portfolio"
            :portfolio="property.portfolio"
            @select="emit('select-portfolio', $event)"
          />
          <View v-if="showManager && property.manager" gap="xs" align="center" class="manager">
            <Avatar :user-name="managerName" show-single-letter size="small" />
            <Text variant="body-small" color="secondary">{{ managerLabel }}</Text>
          </View>
        </View>

        <View gap="sm" align="center" justify="space-between">
          <PropertyStatusLabel :small="true" :status="property.status" />
          <Text variant="body-small" color="secondary">{{ dateInfo }}</Text>
        </View>
      </View>
    </Card>
  </Link>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia';
import pluralize from 'pluralize';
import { computed } from 'vue';

import { Avatar, Card, Link, Text, View } from '@tallo/design-system';
import { teamMemberName } from '@tallo/investor/company';
import { useInvestorStore } from '@tallo/investor/investor';
import { PropertyPhotoPreview } from '@tallo/property';

import { PropertyListItemDto } from '../dtos/property-list-item.dto.interface';
import { propertyRoute } from '../utils/property-route.util';
import { propertyDateLabel, propertyManagerLabel, propertySummary } from '../utils/property-summary.util';
import PortfolioChip from './portfolio-chip.vue';
import PropertyStatusLabel from './property-status-label.vue';

const props = defineProps<{
  property: PropertyListItemDto;
  /** The portfolio the unit is filed in, when it is in one */
  showPortfolio?: boolean;
  /** Who Tallo routes the unit to */
  showManager?: boolean;
  /** In place of the property's name, e.g. its unit number inside its building */
  title?: string;
}>();
const emit = defineEmits<{ 'select-portfolio': [portfolioId: string] }>();

const { investor } = storeToRefs(useInvestorStore());

const managerName = computed(() => (props.property.manager ? teamMemberName(props.property.manager) : ''));
const managerLabel = computed(() => propertyManagerLabel(props.property, investor.value?.id));

const additionalInfo = computed(() => propertySummary(props.property));

const dateInfo = computed(() => propertyDateLabel(props.property));

const propertyLink = computed(() => propertyRoute(props.property));
</script>

<style scoped lang="scss">
.property-card {
  display: flex;
  flex-direction: column;
  gap: var(--t-spacing-md);
}

// A long portfolio name or manager name wraps inside the card rather than widening it
.team {
  min-width: 0;
  overflow-wrap: anywhere;
}

.manager {
  min-width: 0;
}
</style>
